/**
 * Email Verification Step - Step 2 of Onboarding
 */

import React, { useState } from 'react';
import { Button, Input } from '../common';

interface EmailVerificationStepProps {
  email: string;
  onVerify: (code: string) => void;
  onResend?: () => void;
  isLoading?: boolean;
  isResending?: boolean;
  error?: string | null;
}

export const EmailVerificationStep: React.FC<EmailVerificationStepProps> = ({
  email,
  onVerify,
  onResend,
  isLoading = false,
  isResending = false,
  error,
}) => {
  const [code, setCode] = useState('');
  const [localError, setLocalError] = useState('');
  const [resendMessage, setResendMessage] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!code || code.length !== 6) {
      setLocalError('Please enter a valid 6-digit code');
      return;
    }
    setLocalError('');
    onVerify(code);
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="mb-8 text-center">
        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg
            className="w-8 h-8 text-blue-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Verify your email
        </h2>
        <p className="text-gray-500">
          We sent a verification code to <br />
          <span className="font-semibold text-gray-900">{email}</span>
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <Input
            id="code"
            label="Verification Code"
            type="text"
            value={code}
            onChange={(val) =>
              setCode(val.toString().replace(/\D/g, '').slice(0, 6))
            }
            placeholder="Enter 6-digit code"
            className="text-center text-2xl tracking-widest"
            error={localError || (error as string)}
            maxLength={6}
            autoFocus
          />
          <p className="text-xs text-gray-500 mt-2 text-center">
            Depending on your email provider, it might take a few minutes for
            the code to arrive. Check your spam folder if you don&apos;t see it.
          </p>
        </div>

        <Button
          type="submit"
          fullWidth
          size="lg"
          isLoading={isLoading}
          disabled={code.length !== 6}
        >
          Verify Email
        </Button>

        <div className="text-center">
          <button
            type="button"
            disabled={isResending || isLoading}
            className="text-sm text-pos-primary hover:text-pos-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={async () => {
              setResendMessage('');
              if (onResend) {
                await onResend();
                setResendMessage('Code resent! Check your email.');
                setTimeout(() => setResendMessage(''), 5000);
              }
            }}
          >
            {isResending ? 'Sending...' : 'Resend Code'}
          </button>
          {resendMessage && (
            <p className="text-xs text-green-600 mt-2">{resendMessage}</p>
          )}
        </div>
      </form>
    </div>
  );
};
